/**
 * Engagement-oriented difficulty maths — calibrated for arcade “lane runner” arousal curves.
 *
 * **Flow / stretch zone** (Csikszentmihalyi): challenge should creep in, swell mid-session, then plateau.
 * Double `smoothstep` gives an S-curve; `FLOW_ENTRY_POWER` is kept moderate so early runs aren’t a tutorial crawl.
 *
 * **Weber–Fechner flavor**: some channels use slightly *softer* exponents so equal wall-clock steps feel like *tiered*
 * perceived jumps (speed growth) while layout stress can ramp sharper (spawn / closing speeds).
 *
 * **Arousal skew** (Yerkes–Dodson inspired): `stressAggro` rises faster mid-run when motor patterns are habituated — we need
 * cognitive novelty from density, not just raw km/h.
 *
 * **Post-loss relief**: after a hit, aggression multipliers dip briefly to interrupt tilt spirals, then decay with τ.
 *
 * **Variable-ratio timing** (Skinner): micro-jitter on respawn delays reduces perfect rhythmic prediction (habituation).
 */

import * as THREE from "three";
import { DIFFICULTY_RAMP_SEC } from "./constants.js";

/** Stretches the opening into a calmer band — lower = difficulty arrives earlier (less “tutorial valley”). */
const FLOW_ENTRY_POWER = 1.12;

/** Stress curve exponent concentrates threat growth mid/late. */
const STRESS_FOCUS_EXPONENT = 1.18;

/** Speed channel stays slightly gentler — drivers tolerate accel if it ladders predictably. */
const SPEED_EASE_EXPONENT = 0.92;

/** Portion of stress-driven spawn aggression trimmed while relief sits above zero. */
const RELIEF_SPAWN_TRIM = 0.22;

function clamp01(x) {
  return THREE.MathUtils.clamp(x, 0, 1);
}

/** Zero-derivative plateau ramp — no step discontinuities felt as “cheap” spikes on headphones. */
export function smoothstepEdge(t) {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}

function flowSpine(linearT01) {
  const warmed = clamp01(linearT01);
  const entry = Math.pow(warmed, FLOW_ENTRY_POWER);
  return smoothstepEdge(smoothstepEdge(entry));
}

/** Sharper arousal tier for choke-point systems (traffic count, offsets, cadence coupling). */
function stressAggro(flowT01) {
  const w = clamp01(flowT01);
  return smoothstepEdge(Math.pow(w, STRESS_FOCUS_EXPONENT));
}

/**
 * Builds parallel difficulty weights — keep axes separable so live tuning preserves psych intent.
 *
 * @param {number} runElapsedSec
 * @param {number} crashRelief01 0 rested, 1 just wrecked — decays externally
 */
export function getEngagementProfile(runElapsedSec, crashRelief01) {
  const linearT = clamp01(runElapsedSec / DIFFICULTY_RAMP_SEC);
  const flow = flowSpine(linearT);

  /** Softer percept curve for kinetic channels. */
  const speedEase = Math.pow(flow, SPEED_EASE_EXPONENT);

  const stressRaw = stressAggro(flow);

  /** Relief trims stress-spawn aggression but never removes flow entirely once you’ve earned late game. */
  const reliefGate = THREE.MathUtils.clamp(crashRelief01, 0, 1);
  const relievedSpawnStress = clamp01(stressRaw * (1 - RELIEF_SPAWN_TRIM * reliefGate));

  return {
    linearT,
    flow,
    speedEase,
    /** Raw stress ignoring relief — accel can still tick so crash doesn’t feel like “cheap god mode”. */
    stress: stressRaw,
    /** Spawn / density / adversarial offsets use this diminished variant right after collisions. */
    spawnStress: relievedSpawnStress
  };
}

/**
 * Variable-interval flavor micro-jitter for next spawn delay — defeats metronome entrainment.
 *
 * @returns {number} multiplicative scalar ~[0.9, 1.12]
 */
export function variableRatioSpawnJitter() {
  return THREE.MathUtils.lerp(0.9, 1.12, Math.random());
}
